import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

export function Waitlist() {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyPhone, setBusyPhone] = useState(null);

  const refresh = async (pending = onlyPending) => {
    setLoading(true);
    setError('');
    try {
      const d = await callApi(
        `/api/v1/admin/waitlist?only_pending=${pending}&limit=100`,
        { accessToken: session.access_token }
      );
      setItems(d.items);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(onlyPending); }, [onlyPending, session.access_token]);

  const sendInvite = async (phone) => {
    if (!window.confirm(`Send invite to ${phone}?`)) return;
    setBusyPhone(phone);
    try {
      await callApi(
        `/api/v1/admin/waitlist/${encodeURIComponent(phone)}/invite`,
        { method: 'POST', accessToken: session.access_token }
      );
      await refresh(onlyPending);
    } catch (e) {
      setError(`${phone}: ${e.message}`);
    } finally {
      setBusyPhone(null);
    }
  };

  const loadMore = async () => {
    try {
      const d = await callApi(
        `/api/v1/admin/waitlist?only_pending=${onlyPending}&limit=100&cursor=${encodeURIComponent(cursor || '')}`,
        { accessToken: session.access_token }
      );
      setItems((p) => [...p, ...d.items]);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-800">Waitlist</h1>
        <label className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
          />
          Only pending
        </label>
      </div>

      <ErrorBox message={error} />
      <Section>
        {loading ? <LoadingSpinner /> : (
          items.length === 0 ? <EmptyState>Nothing here.</EmptyState> : (
            <div className="divide-y">
              {items.map((w) => (
                <div key={w.phone} className="flex items-center justify-between py-2 text-sm gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-stone-800">{w.phone}</div>
                    <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2">
                      <span>Joined {timeAgo(w.created_at)}</span>
                      {w.palate_user_id && (
                        <>
                          <span className="text-stone-400">·</span>
                          <span>signed up as</span>
                          <UserChip
                            userId={w.palate_user_id}
                            displayName={w.palate_display_name}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  {w.palate_user_id ? (
                    <span className="text-xs text-green-700 whitespace-nowrap">on Palate</span>
                  ) : (
                    <button
                      disabled={busyPhone === w.phone}
                      onClick={() => sendInvite(w.phone)}
                      className="text-xs bg-stone-800 text-white rounded px-3 py-1 disabled:opacity-50"
                    >
                      {busyPhone === w.phone ? 'Sending…' : 'Send invite'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        )}
        {hasMore && (
          <button
            onClick={loadMore}
            className="text-sm text-stone-700 underline mt-2"
          >
            Load more
          </button>
        )}
      </Section>
    </div>
  );
}
